import React, { useEffect, useMemo, useState } from "react";
import {
  calculateLeadScore,
  getLeadTemperature
} from "../utils/crm/leadScoring";
import {
  isOverdue,
  isDueToday,
} from "../utils/crm/dateHelpers";
import {
  getConversionProbability,
} from "../utils/crm/aiSales";
import ExecutiveDashboard from "../components/crm/ExecutiveDashboard";
import ExecutiveBriefing from "../components/crm/ExecutiveBriefing";
import DealRisks from "../components/crm/DealRisks";
import ActivityFeed from "../components/crm/ActivityFeed";
import OpportunityRanking from "../components/crm/OpportunityRanking";
import NotificationBanner from "../components/crm/NotificationBanner";
import LeadTable from "../components/crm/LeadTable";
import LeadDetailDrawer from "../components/crm/LeadDetailDrawer";
import PageHeader from "../components/ui/PageHeader";
import Button from "../components/ui/Button";
import StatCard from "../components/ui/StatCard";
import EmptyState from "../components/ui/EmptyState";
import TextInput from "../components/ui/TextInput";
import Section from "../components/ui/Section";
import { addNotificationOnce } from "../services/notificationService";

const API =
  process.env.NODE_ENV === "production"
    ? "https://ecovanta.onrender.com"
    : "http://localhost:5000";

const PIPELINE_STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];

function getBadgeStyle(value) {
  return {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
    background: value === "Hot" ? "#fee2e2" : value === "Warm" ? "#fef3c7" : "#e5e7eb",
    color: value === "Hot" ? "#991b1b" : value === "Warm" ? "#92400e" : "#374151"
  };
}

function getRowStyle(value) {
  return {
    borderBottom: "1px solid #e5e7eb",
    cursor: "pointer",
    background: value === "Hot" ? "#fff1f2" : value === "Warm" ? "#fffbeb" : "#ffffff"
  };
}



function calculateOpportunityScore(lead) {
  const leadScore = lead.score || calculateLeadScore(lead);
  const probability = getConversionProbability(lead);
  const stage = lead.stage || "New";

  let opportunityScore = leadScore * 0.5 + probability * 0.5;

  if (stage === "Proposal") opportunityScore += 10;
  if (stage === "Qualified") opportunityScore += 7;
  if (stage === "Contacted") opportunityScore += 4;

  if (lead.nextFollowUp) opportunityScore += 3;
  if (lead.lastContactedAt) opportunityScore += 3;

  if (stage === "Won") return 100;
  if (stage === "Lost") return 0;

  return Math.max(0, Math.min(100, Math.round(opportunityScore)));
}

function estimateDealValue(lead) {
  const size = lead.companySize || "";

  if (size === "1000+") return 50000;
  if (size === "251-1000") return 30000;
  if (size === "51-250") return 15000;
  if (size === "1-50") return 5000;

  return 7500;
}

function generateDealRisks(leads) {
  const risks = [];

  leads.forEach((lead) => {
    const company = lead.company || lead.name || "Unknown";
    const probability = getConversionProbability(lead);
    const score = lead.score || calculateLeadScore(lead);

    // High-value lead never contacted
    if (
      score >= 80 &&
      !lead.lastContactedAt &&
      lead.stage !== "Won" &&
      lead.stage !== "Lost"
    ) {
      risks.push({
        severity: "High",
        icon: "🔥",
        company,
        message: "Hot lead has never been contacted."
      });
    }

    // Qualified without follow-up
    if (
      lead.stage === "Qualified" &&
      !lead.nextFollowUp
    ) {
      risks.push({
        severity: "High",
        icon: "📅",
        company,
        message: "Qualified lead has no follow-up scheduled."
      });
    }

    // Proposal without follow-up
    if (
      lead.stage === "Proposal" &&
      !lead.nextFollowUp
    ) {
      risks.push({
        severity: "Medium",
        icon: "📄",
        company,
        message: "Proposal stage but no next follow-up."
      });
    }

    // Overdue follow-up
    if (isOverdue(lead.nextFollowUp)) {
      risks.push({
        severity: "High",
        icon: "⏰",
        company,
        message: "Follow-up is overdue."
      });
    }

    // High probability but no owner
    if (
      probability >= 75 &&
      !lead.owner
    ) {
      risks.push({
        severity: "Medium",
        icon: "👤",
        company,
        message: "High-probability opportunity has no owner assigned."
      });
    }
  });

  return risks;
}

function generateExecutiveBriefing({
  executiveSummary,
  revenueForecast,
  dealRisks,
  notifications,
  topOpportunities
}) {
  const priorities = [];

  // Highest ranked opportunity
  if (topOpportunities.length > 0) {
    priorities.push(
      `Contact ${topOpportunities[0].company} — Opportunity Score ${topOpportunities[0].opportunityScore}.`
    );
  }

  // Highest risk
  if (dealRisks.length > 0) {
    priorities.push(
      `${dealRisks[0].company}: ${dealRisks[0].message}`
    );
  }

  // Follow-ups
  if (notifications.overdueFollowUps.length > 0) {
    priorities.push(
      `${notifications.overdueFollowUps.length} overdue follow-up${
        notifications.overdueFollowUps.length === 1 ? "" : "s"
      } require immediate attention.`
    );
  }

  return {
    greeting: `Good ${new Date().getHours() < 12
      ? "morning"
      : new Date().getHours() < 18
      ? "afternoon"
      : "evening"},`,

    activeLeads: executiveSummary.active,

    expectedRevenue: revenueForecast.expectedRevenue,

    hotLeads: executiveSummary.hot,

    likelyWins: executiveSummary.likelyWins,

    atRisk: executiveSummary.atRisk,

    overdue: notifications.overdueFollowUps.length,

    priorities
  };
}

function generateActivityFeed(leads) {
  const activities = [];

  leads.forEach((lead) => {
    const company = lead.company || lead.name || "Unknown lead";
    const stage = lead.stage || "New";
    const score = lead.score || calculateLeadScore(lead);
    const temperature = lead.temperature || getLeadTemperature(score);
    const probability = getConversionProbability(lead);
    const opportunityScore = calculateOpportunityScore(lead);

    if (temperature === "Hot" && stage !== "Won" && stage !== "Lost") {
      activities.push({
        type: "hot",
        icon: "🔥",
        title: `${company} is a hot lead`,
        detail: `Lead score ${score}, conversion probability ${probability}%.`,
        date: lead.updatedAt || lead.createdAt
      });
    }

    if (lead.nextFollowUp) {
      activities.push({
        type: "follow-up",
        icon: "📞",
        title: `Follow-up scheduled for ${company}`,
        detail: `Next follow-up: ${new Date(
          lead.nextFollowUp
        ).toLocaleDateString()}.`,
        date: lead.nextFollowUp
      });
    }

    if (stage === "Qualified" || stage === "Proposal") {
      activities.push({
        type: "pipeline",
        icon: stage === "Proposal" ? "📄" : "🎯",
        title: `${company} reached ${stage} stage`,
        detail: `Opportunity score ${opportunityScore}.`,
        date: lead.updatedAt || lead.createdAt
      });
    }

    if (Array.isArray(lead.notesTimeline)) {
      lead.notesTimeline.forEach((noteItem) => {
        activities.push({
          type: "note",
          icon: "📝",
          title: `Note added to ${company}`,
          detail: noteItem.note,
          date: noteItem.createdAt || lead.updatedAt || lead.createdAt
        });
      });
    }
  });

  return activities
    .filter((item) => item.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10);
}

export default function LeadManagementDashboard() {
  const [leads, setLeads] = useState([]);
  const [loadStatus, setLoadStatus] = useState("Loading leads...");
  const [leadFilter, setLeadFilter] = useState("All");
  const [selectedLead, setSelectedLead] = useState(null);
  const [crmStatus, setCrmStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [newTimelineNote, setNewTimelineNote] = useState("");
  const [emailDraft, setEmailDraft] = useState(null);
  const [meetingBrief, setMeetingBrief] = useState(null);

  useEffect(() => {
    async function loadLeads() {
      try {
        const response = await fetch(`${API}/request-access`, {
          credentials: "include"
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Unable to load leads.");
        }

        setLeads(Array.isArray(data) ? data : []);
        setLoadStatus("");
      } catch (error) {
        console.error("Load leads failed:", error);
        setLoadStatus(`Failed to load leads: ${error.message}`);
      }
    }

    loadLeads();
  }, []);

  async function updateLeadStage(leadId, stage) {
    try {
      const response = await fetch(`${API}/request-access/${leadId}/stage`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stage })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Unable to update lead stage.");
      }

      setLeads((prev) =>
        prev.map((lead) => (lead._id === leadId ? { ...lead, stage } : lead))
      );

      setSelectedLead((prev) =>
        prev && prev._id === leadId ? { ...prev, stage } : prev
      );
    } catch (error) {
      console.error("Stage update failed:", error);
      alert(`Stage update failed: ${error.message}`);
    }
  }

  async function updateLeadCrmFields() {
    if (!selectedLead?._id) return;

    try {
      setCrmStatus("Saving CRM updates...");

      const response = await fetch(`${API}/request-access/${selectedLead._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          notes: selectedLead.notes || "",
          owner: selectedLead.owner || "",
          nextFollowUp: selectedLead.nextFollowUp || null,
          lastContactedAt: selectedLead.lastContactedAt || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Unable to update CRM fields.");
      }

      setLeads((prev) =>
        prev.map((lead) => (lead._id === data._id ? { ...lead, ...data } : lead))
      );

      setSelectedLead((prev) => (prev ? { ...prev, ...data } : prev));
      setCrmStatus("CRM updates saved.");
    } catch (error) {
      console.error("CRM update failed:", error);
      setCrmStatus(`Save failed: ${error.message}`);
    }
  }

  function exportLeadsCsv() {
  const rows = filteredLeads.map((lead) => ({
    Name: lead.name || "",
    Email: lead.email || "",
    Company: lead.company || "",
    Industry: lead.industry || "",
    CompanySize: lead.companySize || "",
    Score: lead.score || "",
    Status: lead.temperature || "",
    Pipeline: lead.stage || "New",
    Owner: lead.owner || "",
    NextFollowUp: lead.nextFollowUp
      ? new Date(lead.nextFollowUp).toLocaleDateString()
      : "",
    LastContacted: lead.lastContactedAt
      ? new Date(lead.lastContactedAt).toLocaleDateString()
      : "",
    Interest: lead.interest || "",
    Source: lead.source || "",
    Notes: lead.notes || "",
    CreatedAt: lead.createdAt
      ? new Date(lead.createdAt).toLocaleDateString()
      : ""
  }));

  const headers = Object.keys(rows[0] || {
    Name: "",
    Email: "",
    Company: "",
    Industry: "",
    CompanySize: "",
    Score: "",
    Status: "",
    Pipeline: "",
    Owner: "",
    NextFollowUp: "",
    LastContacted: "",
    Interest: "",
    Source: "",
    Notes: "",
    CreatedAt: ""
  });

  const csv = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const value = String(row[header] || "");
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
  ].join("\n");

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `ecovanta-leads-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

async function addTimelineNote() {
  if (!selectedLead?._id) return;

  const note = newTimelineNote.trim();

  if (!note) {
    setCrmStatus("Please enter a note before saving.");
    return;
  }

  try {
    setCrmStatus("Adding timeline note...");

    const response = await fetch(`${API}/request-access/${selectedLead._id}/notes`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ note })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Unable to add timeline note.");
    }

    setLeads((prev) =>
      prev.map((lead) => (lead._id === data._id ? { ...lead, ...data } : lead))
    );

    setSelectedLead((prev) => (prev ? { ...prev, ...data } : prev));
    setNewTimelineNote("");
    setCrmStatus("Timeline note added.");
  } catch (error) {
    console.error("Timeline note failed:", error);
    setCrmStatus(`Timeline note failed: ${error.message}`);
  }
}

  const enrichedLeads = useMemo(() => {
    return leads
      .map((lead) => {
        const score = calculateLeadScore(lead);
        const temperature = getLeadTemperature(score);

        return {
          ...lead,
          score,
          temperature,
          stage: lead.stage || "New"
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [leads]);

const filteredLeads = useMemo(() => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  return enrichedLeads.filter((lead) => {
    const matchesTemperature =
      leadFilter === "All" || lead.temperature === leadFilter;

    const searchableText = [
      lead.name,
      lead.company,
      lead.email,
      lead.owner,
      lead.industry,
      lead.stage,
      lead.interest
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch =
      !normalizedSearch || searchableText.includes(normalizedSearch);

    return matchesTemperature && matchesSearch;
  });
}, [enrichedLeads, leadFilter, searchTerm]);

  const stats = useMemo(() => {
    const today = new Date();

    return {
      total: leads.length,
      today: leads.filter((lead) => {
        const d = new Date(lead.createdAt);
        return (
          d.getDate() === today.getDate() &&
          d.getMonth() === today.getMonth() &&
          d.getFullYear() === today.getFullYear()
        );
      }).length,
      month: leads.filter((lead) => {
        const d = new Date(lead.createdAt);
        return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
      }).length,
      hot: enrichedLeads.filter((lead) => lead.temperature === "Hot").length
    };
  }, [leads, enrichedLeads]);

  const pipelineStats = useMemo(() => {
    return PIPELINE_STAGES.reduce((acc, stage) => {
      acc[stage] = enrichedLeads.filter((lead) => lead.stage === stage).length;
      return acc;
    }, {});
  }, [enrichedLeads]);

const notifications = useMemo(() => {
  const overdueFollowUps = enrichedLeads.filter((lead) =>
    isOverdue(lead.nextFollowUp)
  );

  const dueTodayFollowUps = enrichedLeads.filter((lead) =>
    isDueToday(lead.nextFollowUp)
  );

  const hotUncontactedLeads = enrichedLeads.filter(
    (lead) =>
      lead.temperature === "Hot" &&
      !lead.lastContactedAt &&
      lead.stage !== "Won" &&
      lead.stage !== "Lost"
  );

  return {
    overdueFollowUps,
    dueTodayFollowUps,
    hotUncontactedLeads
  };
}, [enrichedLeads]);

const topOpportunities = useMemo(() => {
  return enrichedLeads
    .filter((lead) => lead.stage !== "Won" && lead.stage !== "Lost")
    .map((lead) => ({
      ...lead,
      opportunityScore: calculateOpportunityScore(lead),
      conversionProbability: getConversionProbability(lead)
    }))
    .sort((a, b) => b.opportunityScore - a.opportunityScore)
    .slice(0, 5);
}, [enrichedLeads]);

const activityFeed = useMemo(() => {
  return generateActivityFeed(enrichedLeads);
}, [enrichedLeads]);

const executiveSummary = useMemo(() => {
  const activeLeads = enrichedLeads.filter(
    (lead) => lead.stage !== "Won" && lead.stage !== "Lost"
  );

  const likelyWins = activeLeads.filter(
    (lead) => getConversionProbability(lead) >= 75
  );

  const atRiskLeads = activeLeads.filter(
    (lead) =>
      isOverdue(lead.nextFollowUp) ||
      (lead.temperature === "Hot" && !lead.lastContactedAt)
  );

  const proposalLeads = activeLeads.filter(
    (lead) => lead.stage === "Proposal"
  );

  const qualifiedLeads = activeLeads.filter(
    (lead) => lead.stage === "Qualified"
  );

  return {
    active: activeLeads.length,
    likelyWins: likelyWins.length,
    atRisk: atRiskLeads.length,
    proposals: proposalLeads.length,
    qualified: qualifiedLeads.length,
    hot: activeLeads.filter((lead) => lead.temperature === "Hot").length
  };
}, [enrichedLeads]);

const revenueForecast = useMemo(() => {
  const activeLeads = enrichedLeads.filter(
    (lead) => lead.stage !== "Won" && lead.stage !== "Lost"
  );

  const forecastItems = activeLeads.map((lead) => {
    const dealValue = estimateDealValue(lead);
    const probability = getConversionProbability(lead);
    const expectedValue = Math.round(dealValue * (probability / 100));

    return {
      ...lead,
      dealValue,
      probability,
      expectedValue
    };
  });

  const totalPipelineValue = forecastItems.reduce(
    (sum, item) => sum + item.dealValue,
    0
  );

  const expectedRevenue = forecastItems.reduce(
    (sum, item) => sum + item.expectedValue,
    0
  );

  return {
    totalPipelineValue,
    expectedRevenue,
    forecastItems
  };
}, [enrichedLeads]);

const dealRisks = useMemo(() => {
  return generateDealRisks(enrichedLeads).slice(0, 8);
}, [enrichedLeads]);

const executiveBriefing = useMemo(() => {
  return generateExecutiveBriefing({
    executiveSummary,
    revenueForecast,
    dealRisks,
    notifications,
    topOpportunities
  });
}, [
  executiveSummary,
  revenueForecast,
  dealRisks,
  notifications,
  topOpportunities
]);

useEffect(() => {
  const todayKey = new Date().toISOString().slice(0, 10);

  notifications.overdueFollowUps.slice(0, 5).forEach((lead) => {
    const leadId = lead._id || lead.id || lead.email || lead.company || lead.name;
    const company = lead.company || lead.name || "Lead";

    addNotificationOnce(
      {
        type: "crm",
        priority: "high",
        title: "CRM follow-up overdue",
        message: `${company} has an overdue follow-up that requires attention.`,
        targetPage: "Follow-Ups"
      },
      `crm-overdue-${todayKey}-${leadId}`
    );
  });

  notifications.dueTodayFollowUps.slice(0, 5).forEach((lead) => {
    const leadId = lead._id || lead.id || lead.email || lead.company || lead.name;
    const company = lead.company || lead.name || "Lead";

    addNotificationOnce(
      {
        type: "crm",
        priority: "normal",
        title: "CRM follow-up due today",
        message: `${company} has a follow-up scheduled for today.`,
        targetPage: "Follow-Ups"
      },
      `crm-due-${todayKey}-${leadId}`
    );
  });

  notifications.hotUncontactedLeads.slice(0, 5).forEach((lead) => {
    const leadId = lead._id || lead.id || lead.email || lead.company || lead.name;
    const company = lead.company || lead.name || "Lead";

    addNotificationOnce(
      {
        type: "crm",
        priority: "high",
        title: "Hot lead needs contact",
        message: `${company} is a hot lead and has not yet been contacted.`,
        targetPage: "Leads CRM"
      },
      `crm-hot-uncontacted-${todayKey}-${leadId}`
    );
  });
}, [notifications]);

  return (
    <main style={{ width: "100%", maxWidth: 1400, margin: "0 auto", padding: "8px 0 40px", minWidth: 0 }}>
<PageHeader
  title="Lead Management Dashboard"
  subtitle="Review Request Access submissions from prospects interested in Ecovanta premium ESG modules."
  actions={
    <Button onClick={exportLeadsCsv} variant="secondary">
      Export CSV
    </Button>
  }
/>

{!loadStatus && (
  <ExecutiveBriefing
    executiveBriefing={executiveBriefing}
  />
)}

{!loadStatus && <NotificationBanner notifications={notifications} />}

      {loadStatus && <p>{loadStatus}</p>}

      {!loadStatus && (
        <>
          <div style={statsGridStyle}>
            <StatCard title="Total Leads" value={stats.total} subtitle="All captured leads" />
            <StatCard title="Leads This Month" value={stats.month} subtitle="Created this month" />
            <StatCard title="Leads Today" value={stats.today} subtitle="Created today" />
            <StatCard title="Hot Leads" value={stats.hot} subtitle="High-priority prospects" badge="Hot" tone="danger" />
          </div>

            <Section
              title="Pipeline Analytics"
              subtitle="Current distribution of leads across the sales pipeline."
            >
              <div style={pipelineGridStyle}>
                {PIPELINE_STAGES.map((stage) => (
                  <StatCard
                    key={stage}
                    title={stage}
                    value={pipelineStats[stage] || 0}
                    subtitle="Leads"
                  />
                ))}
              </div>
            </Section>
        </>
      )}

    {!loadStatus && leads.length === 0 && (
  <EmptyState
    title="No leads yet"
    description="Lead requests will appear here when prospects submit the Request Access form."
  />
)}

      {!loadStatus && (
  <ExecutiveDashboard
    executiveSummary={executiveSummary}
    revenueForecast={revenueForecast}
  />
)}

{!loadStatus && <DealRisks dealRisks={dealRisks} />}

{!loadStatus && <ActivityFeed activityFeed={activityFeed} />}

{!loadStatus && (
  <OpportunityRanking
    topOpportunities={topOpportunities}
    onViewLead={(lead) => {
      setSelectedLead(lead);
      setCrmStatus("");
    }}
  />
)}

<div style={{ marginBottom: 16 }}>
  <TextInput
    placeholder="Search by name, company, email, owner, industry, stage..."
    value={searchTerm}
    onChange={(event) => setSearchTerm(event.target.value)}
    fullWidth
  />
</div>

      {leads.length > 0 && (
        <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 18,
          alignItems: "center",
          flexWrap: "wrap"
        }}
      >
          {["All", "Hot", "Warm", "Cold"].map((filter) => (
            <Button
              key={filter}
              onClick={() => setLeadFilter(filter)}
              variant={leadFilter === filter ? "primary" : "secondary"}
              size="sm"
            >
              {filter}
            </Button>
          ))}

          <span style={{ alignSelf: "center", color: "#64748b" }}>
            Showing {filteredLeads.length} of {leads.length} leads
          </span>

        </div>
      )}

      <LeadTable
      filteredLeads={filteredLeads}
      getRowStyle={getRowStyle}
      getBadgeStyle={getBadgeStyle}
      onSelectLead={(lead) => {
        setSelectedLead(lead);
        setCrmStatus("");
      }}
    />

      {!loadStatus &&
        leads.length > 0 &&
        filteredLeads.length === 0 && (
          <EmptyState
            title="No matching leads"
            description="Try changing the search term or lead filter."
          />
      )}

   <LeadDetailDrawer
  selectedLead={selectedLead}
  setSelectedLead={setSelectedLead}
  crmStatus={crmStatus}
  setCrmStatus={setCrmStatus}
  newTimelineNote={newTimelineNote}
  setNewTimelineNote={setNewTimelineNote}
  emailDraft={emailDraft}
  setEmailDraft={setEmailDraft}
  meetingBrief={meetingBrief}
  setMeetingBrief={setMeetingBrief}
  updateLeadStage={updateLeadStage}
  updateLeadCrmFields={updateLeadCrmFields}
  addTimelineNote={addTimelineNote}
  getBadgeStyle={getBadgeStyle}
/>
    </main>
  );
}

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginTop: 24,
  marginBottom: 32
};

const pipelineGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 16
};